<template>
  <div id="endDeviceMessages">
    <card :title="$t('ed-messages:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-sm-4">
            <label for="period" class="control-label">{{ $t('ed-messages:period') | capitalize }}</label>
            <div class="input-group">
              <flat-pickr id="period"
                          v-model="filterPeriod"
                          :placeholder="$t('ed-messages:periodPlaceholder') | capitalize"
                          :config="fpConfig"
                          class="form-control"
              />
              <div class="input-group-append">
                <button id="resetPeriodButton" class="btn btn-flat btn-sm" type="button" @click="filterPeriod=null"><i class="icon-remove" /></button>
              </div>
            </div>
          </div>

          <div class="form-group col-sm-4">
            <label for="filterDirection" class="control-label">{{ $t('ed-messages:direction') | capitalize }}</label>
            <select id="filterDirection" v-model="filterDirection" class="form-control">
              <option value="" />
              <option value="Uplink">Uplink</option>
              <option value="Downlink">Downlink</option>
            </select>
          </div>

          <div class="form-group col-sm-4">
            <label for="filterType" class="control-label">{{ $t('ed-messages:type') | capitalize }}</label>
            <select id="filterType" v-model="filterType" class="form-control">
              <option value="" />
              <option value="JoinRequest">JoinRequest</option>
              <option value="JoinAccept">JoinAccept</option>
              <option value="UnconfirmedDataUp">UnconfirmedDataUp</option>
              <option value="UnconfirmedDataDown">UnconfirmedDataDown</option>
              <option value="ConfirmedDataUp">ConfirmedDataUp</option>
              <option value="ConfirmedDataDown">ConfirmedDataDown</option>
              <option value="RejoinRequest">RejoinRequest</option>
              <option value="Proprietary">Proprietary</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-sm-2">
            <label for="filterAck" class="control-label">{{ $t('ed-messages:ack') | capitalize }}</label>
            <select id="filterAck" v-model="filterAck" class="form-control">
              <option value="" />
              <option value="true">{{ $t('ed-messages:yes') | capitalize }}</option>
              <option value="false">{{ $t('ed-messages:no') | capitalize }}</option>
            </select>
          </div>

          <div class="form-group col-sm-4">
            <label for="filterMacCommand" class="control-label">{{ $t('ed-messages:macCmd') | capitalize }}</label>
            <select id="filterMacCommand" v-model="filterMacCommand" class="form-control">
              <option value="" />
              <option value="ResetInd">ResetInd</option>
              <option value="ResetConf">ResetConf</option>
              <option value="LinkCheckReq">LinkCheckReq</option>
              <option value="LinkCheckAns">LinkCheckAns</option>
              <option value="LinkADRReq">LinkADRReq</option>
              <option value="LinkADRAns">LinkADRAns</option>
              <option value="DutyCycleReq">DutyCycleReq</option>
              <option value="DutyCycleAns">DutyCycleAns</option>
              <option value="RXParamSetupReq">RXParamSetupReq</option>
              <option value="RXParamSetupAns">RXParamSetupAns</option>
              <option value="DevStatusReq">DevStatusReq</option>
              <option value="DevStatusAns">DevStatusAns</option>
              <option value="NewChannelReq">NewChannelReq</option>
              <option value="NewChannelAns">NewChannelAns</option>
              <option value="RXTimingSetupReq">RXTimingSetupReq</option>
              <option value="RXTimingSetupAns">RXTimingSetupAns</option>
              <option value="TxParamSetupReq">TxParamSetupReq</option>
              <option value="TxParamSetupAns">TxParamSetupAns</option>
              <option value="DlChannelReq">DlChannelReq</option>
              <option value="DlChannelAns">DlChannelAns</option>
              <option value="RekeyInd">RekeyInd</option>
              <option value="RekeyConf">RekeyConf</option>
              <option value="ADRParamSetupReq">ADRParamSetupReq</option>
              <option value="ADRParamSetupAns">ADRParamSetupAns</option>
              <option value="DeviceTimeReq">DeviceTimeReq</option>
              <option value="DeviceTimeAns">DeviceTimeAns</option>
              <option value="ForceRejoinReq">ForceRejoinReq</option>
              <option value="RejoinParamSetupReq">RejoinParamSetupReq</option>
              <option value="RejoinParamSetupAns">RejoinParamSetupAns</option>
              <option value="Proprietary">Proprietary</option>
            </select>
          </div>

          <div class="form-group col-sm-6">
            <label for="filterGateway" class="control-label">{{ $t('ed-messages:gateway') | capitalize }}</label>
            <input id="filterGateway"
                   v-model="filterGateway"
                   v-validate="'text'"
                   :data-vv-as="$t('ed-messages:gateway')"
                   :class="{ 'is-invalid': errors.has('filterGateway') }"
                   name="filterGateway"
                   class="form-control"
                   type="text"
            >
            <div class="invalid-feedback">{{ errors.first('filterGateway') }}</div>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('ed-messages:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="endDeviceMessagesGrid"
          :title="$t('ed-messages:messages')"
          :columns="columns"
          :paginated-dto="rows"
          :wrap-header="true"
          :enable-click-on-row="true"
          :columns-visibility-btn="true"
          @clickOnRow="displayDetailsModal"
          @load="filter"
    >
      <template slot="date" slot-scope="props">{{ props.dto.date | date }}</template>

      <template slot="macCmd" slot-scope="props">
        <template v-if="props.dto.macCommand && props.dto.macCommand.length > 0">
          <a v-for="(macCmd, iMacCmd) in props.dto.macCommand" :key="iMacCmd" href class="d-block" @click.stop.prevent="displayMacCmdModal(macCmd)">{{ macCmd.command | stringify | substring(50) }}</a>
        </template>
        <div v-else>N/A</div>
      </template>

      <template slot="freq" slot-scope="props">
        <div v-for="(rp, indexRp) in props.dto.radioParameters" :key="indexRp" class="multiple-data-cell border-top">{{ rp.freq + ' MHz' }}</div>
      </template>

      <template slot="modulation" slot-scope="props">
        <div v-for="(rp, indexRp) in props.dto.radioParameters" :key="indexRp" class="multiple-data-cell border-top">{{ rp.modulation }}</div>
      </template>

      <template slot="dataRate" slot-scope="props">
        <div v-for="(rp, indexRp) in props.dto.radioParameters" :key="indexRp" class="multiple-data-cell border-top">{{ rp.dataRate }}</div>
      </template>

      <template slot="gateways" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">
          <router-link :to="{name: 'gatewayRoute', params: {eui: gateway.gwEui}}">{{ gateway.gwEui }}</router-link>
        </div>
      </template>

      <template slot="rssi" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">{{ gateway.rssi | number('0,0.[00]','',' dBm') }}</div>
      </template>

      <template slot="snr" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">{{ gateway.snr | number('0,0.[00]','',' dB') }}</div>
      </template>

      <router-link slot="eui" slot-scope="props" :to="{name: 'gatewayRoute', params: {eui: props.dto.gwEui}}">{{ props.dto.gwEui }}</router-link>
    </grid>

    <modal id="detailsModal"
           v-model="showDetailsModal"
           :title="detailsModalTitle"
    >
      <div slot="modal-body">
        <div class="row">
          <label class="col-md-3">{{ $t('ed-messages:encodingType') | capitalize }}</label>
          <div class="col-md-9">{{ $t('ed-messages:' + detailsModalBody.encodingType) | capitalize }}</div>
        </div>
        <div class="row">
          <label class="col-md-3">{{ $t('ed-messages:phyPayload') | capitalize }}</label>
          <div class="col-md-9 payload">{{ detailsModalBody.phyPayload }}</div>
        </div>
      </div>
    </modal>

    <modal id="detailsMacCmdModal"
           v-model="showMacCmdModal"
           :title="macCmdModalTitle"
    >
      <div slot="modal-body">
        <!-- Need to use v-html to insert formatted json (secured content) -->
        <!-- eslint-disable-next-line -->
        <div v-html="macCmdModalBody" />
      </div>
    </modal>
  </div>
</template>

<script>
import { upperFirst, isEmpty } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'

export default {
  titleKey: 'endDeviceMessages',

  props: {
    devEui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      // Filters
      filterPeriod: '',
      filterDirection: '',
      filterType: '',
      filterAck: '',
      filterMacCommand: '',
      filterGateway: '',
      // Display details in modal
      showDetailsModal: false,
      detailsModalTitle: '',
      detailsModalBody: {},
      // Display mac commands in modal
      showMacCmdModal: false,
      macCmdModalTitle: '',
      macCmdModalBody: {},
      // Display events
      rows: {},

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    ...mapGetters(['endDevice']),

    columns () {
      const cols = [
        { text: this.$t('ed-messages:date'), slot: 'date', class: 'wrap' },
        { text: this.$t('ed-messages:direction'), formatter: message => message.direction },
        { text: this.$t('ed-messages:type'), formatter: message => message.type },
        { text: this.$t('ed-messages:macCmd'), slot: 'macCmd' },
        { text: this.$t('ed-messages:fPort'), formatter: message => message.fPort },
        { text: this.$t('ed-messages:fCnt'), formatter: message => message.fCnt },
        {
          text: this.$t('ed-messages:ack'),
          formatter: message => {
            if (message.ack === null) return '-'
            else return upperFirst(message.ack ? this.$t('ed-messages:yes') : this.$t('ed-messages:no'))
          }
        },
        { text: this.$t('ed-messages:freq'), slot: 'freq', class: 'p-0' },
        { text: this.$t('ed-messages:modulation'), slot: 'modulation', class: 'p-0' },
        { text: this.$t('ed-messages:dataRate'), slot: 'dataRate', class: 'p-0' },
        { text: this.$t('ed-messages:gateway'), slot: 'gateways', class: 'p-0' },
        { text: this.$t('ed-messages:rssi'), slot: 'rssi', class: 'p-0' },
        { text: this.$t('ed-messages:snr'), slot: 'snr', class: 'p-0' }
      ]

      if (this.endDevice.classType === 'A' && this.endDevice.supportClassB) {
        cols.push({
          text: this.$t('ed-messages:classB'),
          formatter: message => {
            if (message.direction === 'Downlink') return '-'
            return upperFirst(message.classB ? this.$t('ed-messages:yes') : this.$t('ed-messages:no'))
          }
        })
      }

      cols.push({
        text: this.$t('ed-messages:delayed'),
        formatter: message => {
          if (message.direction === 'Downlink') return '-'
          return upperFirst(message.delayed ? this.$t('ed-messages:yes') : this.$t('ed-messages:no'))
        }
      })

      return cols
    }
  },

  watch: {
    devEui () {
      // Reset data, and get data from new gateway
      this.rows = {}
    },

    endDevice () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.rows)) return

      this.filter()
    }
  },

  created () {
    this.filter()
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add criteria
      if (this.filterDirection) search = this.$utils.addSearch(search, 'eq', this.filterDirection, 'direction')
      if (this.filterType) search = this.$utils.addSearch(search, 'eq', this.filterType, 'type')
      if (this.filterAck) search = this.$utils.addSearch(search, 'eq', this.filterAck, 'ack')
      if (this.filterMacCommand) search = this.$utils.addSearch(search, 'eq', this.filterMacCommand, 'macCommand.name')
      if (this.filterGateway) search = this.$utils.addSearch(search, 'eq', this.filterGateway, 'gwInfo.gwEui')
      // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
      const filterPeriod = this.filterPeriod ? this.filterPeriod.split(' to ').map(v => v * 1000) : []
      if (!isEmpty(filterPeriod)) {
        search = this.$utils.addSearch(search, 'gte', filterPeriod[0], 'date')
        search = this.$utils.addSearch(search, 'lte', filterPeriod[1], 'date')
      }

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getMessages(options)
    },

    async getMessages (options) {
      if (isEmpty(this.endDevice)) return

      const params = this.$utils.getParams({ sort: '-date' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getEndDeviceMessages(this.endDevice, params))
      if (error || !response) return

      this.rows = response.data
    },

    displayDetailsModal (dto) {
      this.detailsModalTitle = upperFirst(this.$t('ed-messages:messageDetails', { message: dto.id }))
      this.detailsModalBody = {
        encodingType: dto.encodingType,
        phyPayload: dto.phyPayload
      }
      this.showDetailsModal = true
    },

    displayMacCmdModal (macCmd) {
      this.macCmdModalTitle = upperFirst(this.$t('ed-messages:messageMacCmd', { macCmd: macCmd.name }))
      this.macCmdModalBody = macCmd.command.split(';').join('<br>')
      this.showMacCmdModal = true
    }
  }
}
</script>
